import { useState, useEffect } from 'react';
import { subscriptionService } from '../lib/subscriptionService';
import {
  Crown,
  AlertCircle,
  Check,
  X,
  Calendar,
  DollarSign,
  Shield,
} from 'lucide-react';

interface SubscriptionManagerProps {
  userId: string;
}

interface Subscription {
  id: string;
  plan_type: string;
  status: string;
  billing_cycle: string;
  price: number;
  cancel_at_period_end: boolean;
  retention_discount_applied: boolean;
  next_billing_date: string;
  current_period_end: string;
}

export function SubscriptionManager({ userId }: SubscriptionManagerProps) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRetentionOffer, setShowRetentionOffer] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, [userId]);

  const loadSubscription = async () => {
    setLoading(true);
    try {
      const data = await subscriptionService.getUserSubscription(userId);
      setSubscription(data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    if (!subscription?.retention_discount_applied) {
      setShowCancelModal(false);
      setShowRetentionOffer(true);
    } else {
      proceedWithCancellation();
    }
  };

  const proceedWithCancellation = async () => {
    setProcessing(true);
    try {
      await subscriptionService.cancelSubscription(userId, false);
      await loadSubscription();
      setShowCancelModal(false);
      setShowRetentionOffer(false);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleAcceptRetentionOffer = async () => {
    if (!agreedToTerms) {
      alert('Please agree to the terms to continue');
      return;
    }

    setProcessing(true);
    try {
      await subscriptionService.acceptRetentionOffer(userId);
      await loadSubscription();
      setShowRetentionOffer(false);
      setAgreedToTerms(false);
    } catch (error) {
      console.error('Error accepting retention offer:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    setProcessing(true);
    try {
      await subscriptionService.reactivateSubscription(userId);
      await loadSubscription();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
        <div className="text-center py-8">
          <Crown className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-6">Upgrade to unlock premium features</p>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg"
          >
            View Plans
          </a>
        </div>
      </div>
    );
  }

  const discountedPrice = subscription.price * 0.5;

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <Crown className="w-5 h-5 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription Management</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200">
            <div className="text-sm text-gray-600 mb-2">Current Plan</div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {subscription.plan_type.replace('_', ' ').toUpperCase()}
            </div>
            <div className="text-sm text-gray-600">
              {subscription.billing_cycle.charAt(0).toUpperCase() + subscription.billing_cycle.slice(1)} billing
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border-2 border-cyan-200">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-cyan-600" />
              <div className="text-sm text-gray-600">Price</div>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              ${subscription.price.toFixed(2)}
              <span className="text-base font-normal text-gray-600">
                /{subscription.billing_cycle === 'yearly' ? 'year' : subscription.billing_cycle === 'weekly' ? 'week' : 'month'}
              </span>
            </div>
            {subscription.retention_discount_applied && (
              <div className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                <Check className="w-3 h-3" />
                50% Discount Applied
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border-2 border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <div className="text-sm text-gray-600">Next Billing Date</div>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {new Date(subscription.next_billing_date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-gray-600" />
              <div className="text-sm text-gray-600">Status</div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                  subscription.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {subscription.status === 'active' ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </span>
            </div>
            {subscription.cancel_at_period_end && (
              <div className="mt-2 text-sm text-gray-600">
                Cancels on {new Date(subscription.current_period_end).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          {subscription.cancel_at_period_end ? (
            <button
              onClick={handleReactivate}
              disabled={processing}
              className="px-6 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? 'Processing...' : 'Reactivate Subscription'}
            </button>
          ) : (
            <button
              onClick={handleCancelClick}
              disabled={processing}
              className="px-6 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel Subscription
            </button>
          )}
          <a
            href="/pricing"
            className="px-6 py-3 bg-gray-200 text-gray-900 rounded-xl font-bold hover:bg-gray-300 transition-all"
          >
            Change Plan
          </a>
        </div>
      </div>

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
              <h3 className="text-2xl font-bold text-gray-900">Cancel Subscription?</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your billing period.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmCancel}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-all disabled:opacity-50"
              >
                Yes, Cancel
              </button>
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-900 rounded-xl font-bold hover:bg-gray-300 transition-all"
              >
                Keep Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {showRetentionOffer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full">
            <div className="text-center mb-6">
              <Crown className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Wait! Special Offer</h3>
              <p className="text-xl text-gray-600">
                Don't leave yet! We'd like to offer you a special deal.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-300 mb-6">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-2">Continue your subscription at</div>
                <div className="text-4xl font-bold text-orange-600 mb-2">
                  50% OFF
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  ${discountedPrice.toFixed(2)}
                  <span className="text-base font-normal text-gray-600">
                    /{subscription.billing_cycle === 'yearly' ? 'year' : subscription.billing_cycle === 'weekly' ? 'week' : 'month'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Was: <span className="line-through">${subscription.price.toFixed(2)}</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                  <Check className="w-5 h-5" />
                  50% Discount Forever
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">
                  I agree. By clicking "Accept Offer", you are providing your electronic signature authorizing ResuSwipe to charge your card as described. You also agree that you will be billed 50% of the premium plan price.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAcceptRetentionOffer}
                disabled={!agreedToTerms || processing}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Processing...' : 'Accept Offer'}
              </button>
              <button
                onClick={proceedWithCancellation}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-900 rounded-xl font-bold hover:bg-gray-300 transition-all disabled:opacity-50"
              >
                No Thanks, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
